import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Observable, catchError, firstValueFrom, of, throwError } from 'rxjs';
import { LabService } from 'src/app/services/lab.service';
import { TemplateProcessorService } from 'src/app/components/report-generation-popup/services/template-processor.service';
import { PdfGeneratorService } from 'src/app/components/report-generation-popup/services/pdf-generator.service';
import { ChainOfCustodyTemplateMapper } from 'src/app/services/template-mappers/entities/chain-of-custody-template-mapper';
import { ChainOfCustodyReportEntity } from 'src/app/services/template-mappers/entities/chain-of-custody-template-mapper.model';
import { ReportTemplate } from 'src/app/models/reportTemplate.model';
import type { TemplatePrintOptions } from 'src/app/models/template.model';
import DOMPurify from 'dompurify';
import { ENTITY_TYPES, getWidgetsForEntityType } from 'src/app/shared/widget-registry';
import { inlineImagesInLayoutHtml } from 'src/app/_helpers/image-inline.helper';
import { CHAIN_OF_CUSTODY_DEFAULT_LAYOUT, CHAIN_OF_CUSTODY_DEFAULT_PRINT_OPTIONS, CHAIN_OF_CUSTODY_PAGE_SIZES } from './chain-of-custody-default-template.constants';

@Injectable()
export class ChainOfCustodyPdfService {
  constructor(
    private labService: LabService,
    private mapper: ChainOfCustodyTemplateMapper,
    private templateProcessor: TemplateProcessorService,
    private pdfGenerator: PdfGeneratorService
  ) { }

  async render(entity: ChainOfCustodyReportEntity): Promise<Blob> {
    const template = await firstValueFrom(this.getTemplate(entity.lab.id));
    const context = await this.mapper.mapToTemplateContext(entity, template);

    const container = document.createElement('div');
    document.body.appendChild(container);
    try {
      container.innerHTML = this.sanitizeLayout(this.templateProcessor.processPlaceholders(template.templateJson.layoutHtml, context));
      const widgetRefs = await this.pdfGenerator.injectWidgets(container, ENTITY_TYPES.CHAIN_OF_CUSTODY, context, false);
      try {
        await this.pdfGenerator.waitForWidgets(widgetRefs);
        const printOptions = template.templateJson.printOptions;
        const inlinedHtml = await inlineImagesInLayoutHtml(container.outerHTML) + this.buildPageSizeRule(printOptions);
        const { blob } = await this.pdfGenerator.generatePdfBlob(inlinedHtml, printOptions, ENTITY_TYPES.CHAIN_OF_CUSTODY);
        return blob;
      } finally {
        widgetRefs.forEach(ref => ref.destroy());
      }
    } finally {
      document.body.removeChild(container);
    }
  }

  private sanitizeLayout(html: string): string {
    const widgetTags = getWidgetsForEntityType(ENTITY_TYPES.CHAIN_OF_CUSTODY).map(widget => widget.tagName);
    return DOMPurify.sanitize(html, { ADD_TAGS: ['style', ...widgetTags], FORCE_BODY: true });
  }

  private buildPageSizeRule(options: TemplatePrintOptions | undefined): string {
    const pageSize = CHAIN_OF_CUSTODY_PAGE_SIZES.includes(options?.pageSize) ? options.pageSize : CHAIN_OF_CUSTODY_DEFAULT_PRINT_OPTIONS.pageSize;
    const pageOrientation = options ? (options.pageOrientation === 'landscape' ? 'landscape' : 'portrait') : CHAIN_OF_CUSTODY_DEFAULT_PRINT_OPTIONS.pageOrientation;
    return `<style>@page { size: ${pageSize} ${pageOrientation}; }</style>`;
  }

  private getTemplate(labId: string): Observable<ReportTemplate> {
    return this.labService.getCocTemplate(labId).pipe(
      catchError((error: unknown) => error instanceof HttpErrorResponse && error.status === HttpStatusCode.NotFound
        ? of(this.buildBuiltInTemplate())
        : throwError(() => error))
    );
  }

  private buildBuiltInTemplate(): ReportTemplate {
    return {
      id: '',
      name: 'Chain of Custody',
      entityType: ENTITY_TYPES.CHAIN_OF_CUSTODY,
      templateJson: {
        status: true,
        layoutHtml: CHAIN_OF_CUSTODY_DEFAULT_LAYOUT,
        assets: [],
        declaredFields: { bound: [], userInputs: {} },
        supportedFilters: [],
        printOptions: CHAIN_OF_CUSTODY_DEFAULT_PRINT_OPTIONS
      },
      isActive: true,
      isShared: false,
      isDefault: true,
      createdBy: '',
      canManage: false,
      createdDate: ''
    };
  }
}
